import { type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Starfield } from './Starfield';

export type PlanetBiome = 'temperate' | 'ice' | 'volcanic' | 'desert' | 'gas' | 'barren';

export interface OrbitFleet {
  id: string;
  /** Where on the orbit ring the marker sits, in degrees. */
  angle: number;
  /** Friendly fleets glow cyan; hostile ones red. */
  hostile?: boolean;
  label?: string;
}

export interface PlanetVisualProps {
  name?: ReactNode;
  /** Coordinates in [ring:system:position] notation. */
  coordinates?: ReactNode;
  biome?: PlanetBiome;
  /** Mean surface temperature — colder worlds yield more Isotop-3 (GDD §1). */
  temperature?: number;
  /** Renders the base moon in a wider orbit. */
  moon?: boolean;
  /** Fleet markers on the orbital ring. */
  fleets?: OrbitFleet[];
  /** Draws the red incoming-threat trajectory across the canvas. */
  threat?: boolean;
  /**
   * Real planet artwork, replacing the procedural gradient sphere. The rim
   * glow, atmosphere halo and terminator shading still layer on top, so art
   * and the biome's accent colour stay consistent — omit for the CSS-only
   * sphere (still the fallback for planets without art yet).
   */
  image?: string;
  /**
   * How much vertical space the canvas may claim.
   *
   * The 16:10 ratio alone is not enough on desktop: in a 1200 px column it
   * produces a 750 px tall canvas that pushes every stat below the fold. These
   * caps keep the scene the emotional anchor of the screen without letting it
   * become the whole screen. `full` opts out for a dedicated planet view.
   */
  size?: 'sm' | 'md' | 'lg' | 'full';
  /** Overlay content — usually an AlertCard docked to a corner. */
  children?: ReactNode;
  className?: string;
}

const SIZES = {
  sm: 'max-h-44 sm:max-h-56',
  md: 'max-h-48 sm:max-h-72 xl:max-h-80',
  lg: 'max-h-56 sm:max-h-[26rem]',
  full: '',
} as const;

/**
 * The central orbit canvas (GDD §4, "Interaktiver Orbit-Canvas").
 *
 * Rendered with layered CSS gradients and SVG rather than WebGL: it holds the
 * middle of the screen at all times, so it has to stay cheap enough to run
 * beside the rest of the HUD on a mid-range phone under Capacitor. Every layer
 * is transform/opacity only, so the whole scene composites on the GPU.
 *
 * The biome gradients double as gameplay information — an ice world reads as
 * blue-white, which is also the visual cue for high Isotop-3 yield.
 */
const BIOMES: Record<PlanetBiome, { body: string; rim: string; atmosphere: string }> = {
  temperate: {
    body: 'radial-gradient(circle at 33% 28%, #3d7f8f 0%, #1d4a58 42%, #0a1e28 78%, #050d12 100%)',
    rim: 'rgb(var(--sg-silicate) / 0.5)',
    atmosphere: 'rgb(var(--sg-silicate) / 0.28)',
  },
  ice: {
    body: 'radial-gradient(circle at 33% 28%, #cfe9f5 0%, #7fb4cf 38%, #2d5a76 74%, #0a1a26 100%)',
    rim: 'rgb(var(--sg-isotope) / 0.6)',
    atmosphere: 'rgb(var(--sg-isotope) / 0.3)',
  },
  volcanic: {
    body: 'radial-gradient(circle at 33% 28%, #f0a04b 0%, #b4451f 38%, #55160f 74%, #180705 100%)',
    rim: 'rgb(var(--sg-titan) / 0.6)',
    atmosphere: 'rgb(var(--sg-titan) / 0.3)',
  },
  desert: {
    body: 'radial-gradient(circle at 33% 28%, #e0c088 0%, #b08b4a 40%, #5c4321 76%, #1b1409 100%)',
    rim: 'rgb(var(--sg-titan) / 0.45)',
    atmosphere: 'rgb(var(--sg-titan) / 0.22)',
  },
  gas: {
    body: 'radial-gradient(circle at 33% 28%, #c9a4e8 0%, #8b5fb0 38%, #432a5c 76%, #150c1e 100%)',
    rim: 'rgb(var(--sg-aether) / 0.55)',
    atmosphere: 'rgb(var(--sg-aether) / 0.3)',
  },
  barren: {
    body: 'radial-gradient(circle at 33% 28%, #9aa5b4 0%, #5d6774 40%, #2a313b 76%, #0b0e12 100%)',
    rim: 'rgb(var(--sg-obsidian-300) / 0.4)',
    atmosphere: 'rgb(var(--sg-obsidian-300) / 0.18)',
  },
};

export function PlanetVisual({
  name,
  coordinates,
  biome = 'temperate',
  temperature,
  moon = false,
  fleets = [],
  threat = false,
  size = 'md',
  image,
  children,
  className,
}: PlanetVisualProps) {
  const skin = BIOMES[biome];

  return (
    <div
      className={cn(
        'relative aspect-[16/10] w-full overflow-hidden rounded-panel border border-edge bg-void',
        // The cap wins over the aspect ratio on wide screens; the scene inside
        // is sized off the container's HEIGHT, so the planet stays circular and
        // simply gets less empty space around it.
        SIZES[size],
        className,
      )}
    >
      <Starfield density={90} nebula />

      {/* Orbital plane */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative aspect-square h-[86%]">
          {/* Fleet orbit ring */}
          <div
            className={cn(
              'absolute inset-[6%] rounded-full border border-dashed',
              threat ? 'border-danger/30' : 'border-silicate/20',
            )}
          />

          {/* Moon orbit — slow, counter-rotating for depth */}
          {moon ? (
            <div className="absolute inset-0 motion-safe:animate-orbit-slow">
              <div
                className="absolute left-1/2 top-0 h-[13%] w-[13%] -translate-x-1/2 rounded-full"
                style={{
                  background:
                    'radial-gradient(circle at 35% 30%, #b9c2ce 0%, #6f7a89 45%, #2b323c 100%)',
                  boxShadow: '0 0 14px rgb(0 0 0 / 0.7)',
                }}
              />
            </div>
          ) : null}

          {/* Fleet markers */}
          {fleets.map((fleet) => (
            <div
              key={fleet.id}
              className="absolute inset-[6%]"
              style={{ transform: `rotate(${fleet.angle}deg)` }}
            >
              <span
                title={fleet.label}
                className={cn(
                  'absolute left-1/2 top-0 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full',
                  fleet.hostile
                    ? 'bg-danger shadow-glow-danger motion-safe:animate-pulse-glow'
                    : 'bg-silicate shadow-glow',
                )}
              />
            </div>
          ))}

          {/* The planet */}
          <div className="absolute inset-[18%]">
            {/* Atmosphere halo */}
            <div
              className="absolute -inset-[9%] rounded-full blur-md"
              style={{ background: `radial-gradient(circle, ${skin.atmosphere} 0%, transparent 68%)` }}
            />
            <div
              className="relative h-full w-full overflow-hidden rounded-full"
              style={{
                background: image ? undefined : skin.body,
                boxShadow: `inset -14px -14px 34px rgb(0 0 0 / 0.75), 0 0 0 1px ${skin.rim}, 0 0 42px ${skin.atmosphere}`,
              }}
            >
              {image ? (
                <img src={image} alt="" className="h-full w-full object-cover" />
              ) : null}
              {/* Terminator — the lit/unlit boundary that sells it as a sphere */}
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background:
                    'linear-gradient(115deg, transparent 0%, transparent 44%, rgb(0 0 0 / 0.55) 72%, rgb(0 0 0 / 0.8) 100%)',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Incoming threat trajectory */}
      {threat ? (
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox="0 0 100 62"
          preserveAspectRatio="none"
          aria-hidden
        >
          <defs>
            <linearGradient id="sg-threat" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="rgb(244 63 63)" stopOpacity="0" />
              <stop offset="100%" stopColor="rgb(244 63 63)" stopOpacity="0.95" />
            </linearGradient>
          </defs>
          <path
            d="M2 4 Q 38 14 50 31"
            fill="none"
            stroke="url(#sg-threat)"
            strokeWidth="0.7"
            strokeDasharray="2.5 1.8"
            className="motion-safe:animate-pulse-glow"
          />
        </svg>
      ) : null}

      {/* Identification plate */}
      {name || coordinates ? (
        <div className="absolute left-3 top-3 rounded-control border border-edge/70 bg-surface-overlay/75 px-2.5 py-1.5 backdrop-blur-sm">
          {name ? (
            <div className="font-display text-xs font-semibold uppercase tracking-[0.12em] text-content">
              {name}
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            {coordinates ? (
              <span data-sg-numeric className="text-label text-content-muted">
                {coordinates}
              </span>
            ) : null}
            {temperature !== undefined ? (
              <span data-sg-numeric className="text-label text-content-muted">
                {temperature > 0 ? '+' : ''}
                {temperature} °C
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {children ? <div className="absolute inset-x-3 bottom-3">{children}</div> : null}
    </div>
  );
}
